import { useState, useEffect } from 'react';
import { FileText, Save, Eye, Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/adminApi';

const PolicyEditor = () => {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', content: '' });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await adminAPI.getPolicies();
      setPolicies(response.data.policies || []);
      if (response.data.policies?.length > 0 && !selectedPolicy) {
        selectPolicy(response.data.policies[0]);
      }
    } catch (error) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const selectPolicy = (policy) => {
    setSelectedPolicy(policy);
    setEditForm({
      title: policy.title,
      content: policy.content,
    });
  };

  const handleSave = async () => {
    if (!selectedPolicy) return;

    setSaving(true);
    try {
      await adminAPI.updatePolicy(selectedPolicy.slug, editForm);
      toast.success('Policy saved successfully');

      // Update local state
      setPolicies(policies.map(p =>
        p.slug === selectedPolicy.slug
          ? { ...p, title: editForm.title, content: editForm.content, last_updated: new Date().toISOString() }
          : p
      ));
      setSelectedPolicy({ ...selectedPolicy, title: editForm.title, content: editForm.content });
    } catch (error) {
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open(`/policy/${selectedPolicy.slug}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Policy Pages</h2>
          <p className="text-gray-600 mt-1">Edit legal and policy pages content</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Policy List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-700">Pages</h3>
            </div>
            <div className="p-2">
              {policies.map((policy) => (
                <button
                  key={policy.slug}
                  onClick={() => selectPolicy(policy)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 ${
                    selectedPolicy?.slug === policy.slug
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <FileText className="w-5 h-5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{policy.title}</div>
                    <div className="text-xs text-gray-500">/{policy.slug}</div>
                  </div>
                  {policy.content ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {selectedPolicy ? (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-700">Edit: {selectedPolicy.title}</h3>
                  <p className="text-sm text-gray-500">
                    Last updated: {new Date(selectedPolicy.last_updated).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePreview}
                    className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Page Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter page title"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content (HTML supported)
                  </label>
                  <textarea
                    value={editForm.content}
                    onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                    rows={20}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="Enter policy content (HTML is supported)"
                  />
                </div>

                {/* Help text */}
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                  <p className="font-medium mb-2">Formatting Tips:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><code className="bg-gray-200 px-1 rounded">&lt;h2&gt;</code> for section headings</li>
                    <li><code className="bg-gray-200 px-1 rounded">&lt;p&gt;</code> for paragraphs</li>
                    <li><code className="bg-gray-200 px-1 rounded">&lt;ul&gt;&lt;li&gt;</code> for bullet lists</li>
                    <li><code className="bg-gray-200 px-1 rounded">&lt;strong&gt;</code> for bold text</li>
                    <li><code className="bg-gray-200 px-1 rounded">&lt;a href="..."&gt;</code> for links</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Select a policy page to edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyEditor;
